import { ref, watch } from 'vue';
import type { ThemePreference } from '@webmail/shared';
import { api } from './api';
import { session } from './session';

/**
 * Darstellung (hell/dunkel).
 *
 * - "system" folgt dem Betriebssystem (prefers-color-scheme), "light"/"dark"
 *   setzen <html data-theme="…"> und überschreiben das.
 * - Ohne Anmeldung wird die Wahl im Browser gespeichert (localStorage).
 *   public/theme-init.js setzt sie schon vor dem ersten Zeichnen.
 * - Angemeldet wird sie zusätzlich pro Postfach gespeichert und nach dem
 *   Login übernommen (falls dort schon einmal gewählt).
 */
const STORAGE_KEY = 'wm-theme';
export const THEMES: ThemePreference[] = ['system', 'light', 'dark'];

function stored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export const theme = ref<ThemePreference>(stored());

/** Nur anzeigen und im Browser merken (ohne Server) */
export function applyTheme(t: ThemePreference): void {
  theme.value = t;
  const root = document.documentElement;
  if (t === 'system') delete root.dataset.theme;
  else root.dataset.theme = t;
  try {
    if (t === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, t);
  } catch {
    // Speicher gesperrt (privater Modus o. ä.): gilt dann nur für diese Seite
  }
}

/** Auswahl des Nutzers: anzeigen und, wenn angemeldet, im Postfach speichern */
export async function setTheme(t: ThemePreference): Promise<void> {
  applyTheme(t);
  if (session.value) await api.saveSettings({ theme: t }).catch(() => undefined);
}

/** Nächste Stufe für den Umschalter im Kopf: System → Hell → Dunkel → System */
export function nextTheme(t: ThemePreference): ThemePreference {
  return THEMES[(THEMES.indexOf(t) + 1) % THEMES.length]!;
}

/** Nach Login (oder Seitenaufruf mit Sitzung) die gespeicherte Darstellung übernehmen */
export function setupUserTheme(): void {
  watch(
    () => session.value?.email,
    async (email) => {
      if (!email) return;
      const saved = await api
        .settings()
        .then((s) => s.theme)
        .catch(() => null);
      // Nie gewählt: aktuelle Browser-Einstellung behalten
      if (saved && session.value?.email === email) applyTheme(saved);
    },
    { immediate: true },
  );
}
